import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/layout/Header';
import { StatCard } from '../../components/common/Common';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import {
  Users, Target, Sparkles, UserCheck, UserX, Phone, ArrowUpRight,
  Calendar, Clock, Activity, Building2, Zap, Download,
  TrendingUp, AlertTriangle, Trophy,
  Facebook, Instagram, Globe, MessageCircle, MapPin, Mail, Linkedin, Youtube, Twitter
} from 'lucide-react';
import { apiFetch, getFileUrl } from '../../utils/api';
import './Dashboard.css';

// Refined harmonious palette — used for all charts/visualizations
const COLORS = [
  '#2a5298',  // navy 500 (primary)
  '#d4a819',  // gold 600 (accent)
  '#10b981',  // emerald
  '#6366f1',  // indigo
  '#f59e0b',  // amber
  '#06b6d4',  // cyan
  '#8b5cf6',  // violet
  '#ec4899',  // pink
  '#3b82f6',  // blue
  '#84cc16',  // lime
];

// Real-estate portal logos kept as bold initials with brand colors
// (these portals don't have universal vector icons; their wordmark IS the brand).
// Social/web sources use real lucide brand icons.
const SOURCE_ICONS = {
  '99acres':      { bg: '#ef6724', text: '99', color: '#fff' },
  'magicbricks':  { bg: '#dc2626', text: 'MB', color: '#fff' },
  'housing':      { bg: '#7c3aed', text: 'H',  color: '#fff' },
  'no broker':    { bg: '#f97316', text: 'NB', color: '#fff' },
  'nobroker':     { bg: '#f97316', text: 'NB', color: '#fff' },
  'square yards': { bg: '#0d9488', text: 'SY', color: '#fff' },
  'squareyards':  { bg: '#0d9488', text: 'SY', color: '#fff' },
  'commonfloor':  { bg: '#1e3a8a', text: 'CF', color: '#fff' },
  'olx':          { bg: '#84d420', text: 'olx', color: '#002f34' },
  'quikr':        { bg: '#003b6f', text: 'Q',  color: '#fff' },
  'google ads':   { bg: '#fff',    text: 'G',  color: '#4285f4', border: '#e5e7eb' },
  'google':       { bg: '#fff',    text: 'G',  color: '#4285f4', border: '#e5e7eb' },
  'facebook':     { bg: '#1877f2', icon: Facebook, color: '#fff' },
  'fb':           { bg: '#1877f2', icon: Facebook, color: '#fff' },
  'instagram':    { bg: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', icon: Instagram, color: '#fff' },
  'whatsapp':     { bg: '#25d366', icon: MessageCircle, color: '#fff' },
  'website':      { bg: '#0ea5e9', icon: Globe, color: '#fff' },
  'web':          { bg: '#0ea5e9', icon: Globe, color: '#fff' },
  'referral':     { bg: '#10b981', icon: UserCheck, color: '#fff' },
  'walk-in':      { bg: '#f59e0b', icon: MapPin, color: '#fff' },
  'walkin':       { bg: '#f59e0b', icon: MapPin, color: '#fff' },
  'api':          { bg: '#10b981', icon: Zap, color: '#fff' },
  'email':        { bg: '#6366f1', icon: Mail, color: '#fff' },
  'linkedin':     { bg: '#0a66c2', icon: Linkedin, color: '#fff' },
  'youtube':      { bg: '#ff0000', icon: Youtube, color: '#fff' },
  'twitter':      { bg: '#1da1f2', icon: Twitter, color: '#fff' },
  'x':            { bg: '#0f172a', icon: Twitter, color: '#fff' },
  'phone':        { bg: '#0ea5e9', icon: Phone, color: '#fff' },
  'call':         { bg: '#0ea5e9', icon: Phone, color: '#fff' },
};

const getSourceIcon = (source) => {
  const key = (source || '').toLowerCase().trim();
  if (SOURCE_ICONS[key]) return SOURCE_ICONS[key];
  const initial = (source || '?').charAt(0).toUpperCase();
  const colorIdx = (source?.length || 0) % COLORS.length;
  return { bg: COLORS[colorIdx] + '20', text: initial, color: COLORS[colorIdx] };
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const Dashboard = () => {
  const { categoryCounts, masterData } = useCRM();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [overdueData, setOverdueData] = useState({ count: 0, overdueFollowups: [] });
  const [trendRows, setTrendRows] = useState([]);
  const [performers, setPerformers] = useState([]);

  useEffect(() => { loadDashboard(); }, []);

  // Dashboard stays mounted while the user works elsewhere; refetch when the
  // tab/window regains focus so stats are not stale on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadDashboard();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, overdueRes, trendRes, perfRes] = await Promise.all([
        apiFetch('dashboard/summary').catch(() => ({ success: false })),
        apiFetch('dashboard/overdue-followups').catch(() => ({ success: false })),
        apiFetch('dashboard/lead-trend').catch(() => ({ success: false })),
        apiFetch('dashboard/top-performers').catch(() => ({ success: false })),
      ]);
      if (dashRes.success) setDashData(dashRes.data);
      if (overdueRes.success) setOverdueData(overdueRes.data);
      if (trendRes.success) setTrendRows(trendRes.data?.trend || []);
      if (perfRes.success) setPerformers(perfRes.data?.performers || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
  };

  const stats = dashData?.stats || {};
  const pipeline = dashData?.pipeline || [];
  const leadSources = dashData?.leadSources || [];
  const upcomingFollowups = dashData?.upcomingFollowups || [];
  const overdueCount = overdueData.count || 0;
  const projectLeads = dashData?.projectLeads || [];
  const topPerformers = performers || [];
  const todayActivity = dashData?.todayActivity || {};
  const statuses = masterData?.statuses || [];

  // Build 30-day trend with zero-fill for missing dates.
  // Keys use LOCAL calendar date (not UTC) to match the server's DATE(create_dt)
  // grouping — toISOString() would shift the bucket by a day in IST.
  const toLocalKey = (val) => {
    if (typeof val === 'string') return val.slice(0, 10);
    const d = val instanceof Date ? val : new Date(val);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const trendData = (() => {
    const map = new Map();
    trendRows.forEach(t => {
      map.set(toLocalKey(t.date), t.count);
    });
    const out = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      out.push({
        date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        count: map.get(toLocalKey(d)) || 0,
      });
    }
    return out;
  })();
  const trendTotal = trendData.reduce((s, d) => s + d.count, 0);

  const pipelineData = pipeline.length > 0
    ? pipeline.filter(p => p.count > 0).map(p => ({ stage: p.label || p.name, count: p.count, color: p.color || '#2a5298' }))
    : statuses.slice(0, 8).filter(s => s.count > 0).map(s => ({ stage: s.name, count: s.count || 0, color: s.color || '#2a5298' }));

  const sourceData = leadSources.map(s => ({ src_id: s.src_id || null, source: s.source || 'Unknown', count: s.count || 0 }));
  const totalSourceLeads = sourceData.reduce((s, d) => s + d.count, 0);
  const projectData = projectLeads.filter(p => p.count > 0).slice(0, 8);

  // Lead status breakdown — share of total leads, largest stage first
  const pipelineTotal = pipeline.reduce((s, p) => s + (p.count || 0), 0);
  const funnelStages = pipeline.length > 0
    ? [...pipeline].filter(p => p.count > 0).sort((a, b) => b.count - a.count).slice(0, 5)
    : [];
  const funnelTop = funnelStages[0]?.count || 1;

  const today = new Date();
  const todayStr = today.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const formatDate = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  const formatTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const userName = user?.full_name || user?.username || 'User';

  return (
    <div>
      <Header title="Dashboard" subtitle="Business overview & CRM analytics" />

      <div className="page">

        {/* ── Greeting Bar (compact, navy gradient) ── */}
        <div className="dash-greet">
          <div className="dash-greet-left">
            <div className="dash-greet-text">
              <span className="dash-greet-hi">{getGreeting()},</span>
              <h2 className="dash-greet-name">{userName}</h2>
            </div>
          </div>
          <div className="dash-greet-right">
            <div className="dash-greet-date">
              <Calendar size={13} />
              <span>{todayStr}</span>
            </div>
          </div>
        </div>

        {/* ── Top 2-column row: stats left, today's activity right ── */}
        <div className="dash-top-row">

          {/* LEFT: Primary + Secondary stats */}
          <div className="dash-top-left">
            {/* Primary stats — 3 large cards */}
            <div className="dash-primary-stats">
              <StatCard icon={Users} label="Total Leads" value={stats.totalLeads || categoryCounts.total || 0} onClick={() => navigate('/leads')} />
              <StatCard icon={Target} label="Today's Leads" value={stats.todayLeads || 0} onClick={() => navigate('/leads/today')} />
              <StatCard icon={AlertTriangle} label="Overdue" value={overdueCount} onClick={() => navigate('/followup/missed')} />
            </div>

            {/* Secondary stats — 4 smaller cards */}
            <div className="dash-secondary-stats">
              <div className="dash-sec-card" onClick={() => navigate('/leads/fresh')}>
                <div className="dash-sec-icon dash-sec-icon--pink"><Sparkles size={15} /></div>
                <div className="dash-sec-info">
                  <span className="dash-sec-label">New Lead</span>
                  <span className="dash-sec-value">{categoryCounts.fresh || 0}</span>
                </div>
              </div>
              <div className="dash-sec-card" onClick={() => navigate('/leads/imported')}>
                <div className="dash-sec-icon dash-sec-icon--slate"><Download size={15} /></div>
                <div className="dash-sec-info">
                  <span className="dash-sec-label">Imported</span>
                  <span className="dash-sec-value">{categoryCounts.imported || 0}</span>
                </div>
              </div>
              <div className="dash-sec-card" onClick={() => navigate('/leads/assigned')}>
                <div className="dash-sec-icon dash-sec-icon--green"><UserCheck size={15} /></div>
                <div className="dash-sec-info">
                  <span className="dash-sec-label">Assigned</span>
                  <span className="dash-sec-value">{categoryCounts.assigned || 0}</span>
                </div>
              </div>
              <div className="dash-sec-card" onClick={() => navigate('/leads/non-assigned')}>
                <div className="dash-sec-icon dash-sec-icon--red"><UserX size={15} /></div>
                <div className="dash-sec-info">
                  <span className="dash-sec-label">Non-Assigned</span>
                  <span className="dash-sec-value">{categoryCounts.nonAssigned || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Today's Activity card */}
          <div className="dash-today-card">
            <div className="dash-today-head">
              <h3 className="dash-section-title">Today's Activity</h3>
              <span className="dash-live-badge">
                <span className="dash-live-dot" />
                Live
              </span>
            </div>
            <div className="dash-today-grid">
              <div className="dash-today-item">
                <div className="dash-today-icon dash-today-icon--blue"><Zap size={18} /></div>
                <div>
                  <span className="dash-today-val">{todayActivity.newLeads || 0}</span>
                  <span className="dash-today-lbl">New Leads</span>
                </div>
              </div>
              <div className="dash-today-item">
                <div className="dash-today-icon dash-today-icon--orange"><Phone size={18} /></div>
                <div>
                  <span className="dash-today-val">{todayActivity.todayFollowups || 0}</span>
                  <span className="dash-today-lbl">Follow-ups</span>
                </div>
              </div>
              <div className="dash-today-item">
                <div className="dash-today-icon dash-today-icon--green"><Activity size={18} /></div>
                <div>
                  <span className="dash-today-val">{todayActivity.totalActivities || 0}</span>
                  <span className="dash-today-lbl">Activities</span>
                </div>
              </div>
              <div className="dash-today-item">
                <div className="dash-today-icon dash-today-icon--purple"><Building2 size={18} /></div>
                <div>
                  <span className="dash-today-val">{projectLeads.length}</span>
                  <span className="dash-today-lbl">Active Projects</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Source-wise Lead Chips ── */}
        {sourceData.length > 0 && (
          <div className="dash-source-chips-wrap">
            <div className="dash-source-chips-head">
              <h3 className="dash-section-title">Leads by Source</h3>
              <span className="dash-section-meta">{sourceData.length} sources</span>
            </div>
            <div className="dash-source-chips">
              {sourceData.slice(0, 12).map((s, i) => {
                const ic = getSourceIcon(s.source);
                const IconComp = ic.icon;
                return (
                  <div
                    key={i}
                    className="dash-source-chip"
                    onClick={() => s.src_id
                      ? navigate(`/leads/source/${s.src_id}`, { state: { sourceName: s.source } })
                      : navigate('/leads')
                    }
                  >
                    <div
                      className="dash-source-chip-icon"
                      style={{
                        background: ic.bg,
                        color: ic.color,
                        border: ic.border ? `1px solid ${ic.border}` : 'none',
                      }}
                    >
                      {IconComp ? <IconComp size={18} strokeWidth={2.2} /> : ic.text}
                    </div>
                    <div className="dash-source-chip-info">
                      <span className="dash-source-chip-name">{s.source}</span>
                      <span className="dash-source-chip-count">{s.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Conversion Funnel + Top Performers ── */}
        <div className="dash-row-2">
          {/* Conversion Funnel */}
          {funnelStages.length > 0 ? (
            <div className="card dash-funnel-card">
              <div className="card__header">
                <h3 className="card__title">
                  <TrendingUp size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Lead Status Breakdown
                </h3>
                <button className="btn btn--ghost btn--sm" onClick={() => navigate('/leads/status-wise')}>
                  View Details <ArrowUpRight size={14} />
                </button>
              </div>
              <div className="card__body">
                <div className="dash-funnel">
                  {funnelStages.map((stage, i) => {
                    const widthPct = funnelTop > 0 ? Math.max(15, (stage.count / funnelTop) * 100) : 15;
                    const conversionPct = pipelineTotal > 0 ? Math.round((stage.count / pipelineTotal) * 100) : 0;
                    return (
                      <div
                        key={stage.ls_id || i}
                        className="dash-funnel-row"
                        onClick={() => navigate(stage.ls_id ? `/leads/status-wise?status=${stage.ls_id}` : '/leads/status-wise')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="dash-funnel-label">
                          <span className="dash-funnel-stage">{stage.label || stage.name}</span>
                          <span className="dash-funnel-pct">{conversionPct}%</span>
                        </div>
                        <div className="dash-funnel-bar-wrap">
                          <div
                            className="dash-funnel-bar"
                            style={{ width: `${widthPct}%`, background: COLORS[i % COLORS.length] }}
                          >
                            <span className="dash-funnel-count">{stage.count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card__body">
                <div className="dash-empty">No funnel data yet</div>
              </div>
            </div>
          )}

          {/* Top Performers */}
          <div className="card">
            <div className="card__header">
              <h3 className="card__title">
                <Trophy size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: '#d4a819' }} />
                Top Performers
              </h3>
            </div>
            <div className="card__body" style={{ padding: 0 }}>
              {topPerformers.length > 0 ? (
                <div className="dash-perf-list">
                  {topPerformers.map((p, i) => {
                    const initial = (p.full_name || p.username || '?').trim().charAt(0).toUpperCase();
                    const medalColor = i === 0 ? '#d4a819' : i === 1 ? '#94a3b8' : i === 2 ? '#cd7f32' : '#cbd5e1';
                    return (
                      <div key={p.u_id} className="dash-perf-row">
                        <div className="dash-perf-rank" style={{ background: medalColor + '20', color: medalColor }}>
                          {i + 1}
                        </div>
                        <div className="dash-perf-avatar">
                          {p.profile_image ? (
                            <img src={getFileUrl(p.profile_image)} alt={p.full_name || p.username} />
                          ) : (
                            <span>{initial}</span>
                          )}
                        </div>
                        <div className="dash-perf-info">
                          <span className="dash-perf-name">{p.full_name || p.username}</span>
                          <span className="dash-perf-role">{p.role_name || '—'}</span>
                        </div>
                        <div className="dash-perf-stats">
                          <span className="dash-perf-count">{p.assigned_count}</span>
                          <span className="dash-perf-lbl">leads</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="dash-empty">No performance data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* ── Lead Trend (full-width, taller) ── */}
        <div className="card dash-trend-full">
          <div className="card__header">
            <h3 className="card__title">
              <TrendingUp size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Lead Trend (30 Days)
            </h3>
            <span className="dash-section-meta">{trendTotal} leads</span>
          </div>
          <div className="card__body">
            {trendTotal > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trendData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2a5298" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#2a5298" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--gray-500)' }}
                    interval={4}
                  />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--gray-500)' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid var(--gray-200)', background: 'var(--white)', color: 'var(--gray-800)', fontSize: '12px' }} />
                  <Area type="monotone" dataKey="count" stroke="#2a5298" strokeWidth={2} fill="url(#trendGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="dash-empty">No leads in last 30 days</div>
            )}
          </div>
        </div>

        {/* ── Pipeline bar + Sources pie ── */}
        <div className="dash-charts">
          {/* Pipeline */}
          <div className="card">
            <div className="card__header">
              <h3 className="card__title">Lead Pipeline</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => navigate('/leads/status-wise')}>View All <ArrowUpRight size={14} /></button>
            </div>
            <div className="card__body">
              {pipelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={pipelineData} barSize={28}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" vertical={false} />
                    <XAxis dataKey="stage" tick={{ fontSize: 10, fill: 'var(--gray-500)' }} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--gray-500)' }} />
                    <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid var(--gray-200)', background: 'var(--white)', color: 'var(--gray-800)', fontSize: '12px' }} />
                    <Bar
                      dataKey="count"
                      radius={[6, 6, 0, 0]}
                      style={{ cursor: 'pointer' }}
                      onClick={(d) => {
                        const match = pipeline.find(p => (p.label || p.name) === d?.stage)
                          || statuses.find(s => s.name === d?.stage);
                        navigate(match?.ls_id ? `/leads/status-wise?status=${match.ls_id}` : '/leads/status-wise');
                      }}
                    >
                      {pipelineData.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="dash-empty">No pipeline data yet</div>
              )}
            </div>
          </div>

          {/* Source Distribution */}
          <div className="card">
            <div className="card__header">
              <h3 className="card__title">Lead Sources</h3>
            </div>
            <div className="card__body">
              {sourceData.length > 0 ? (
                <div className="dash-sources">
                  <div className="dash-pie">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={sourceData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="count" paddingAngle={2}>
                          {sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid var(--gray-200)', background: 'var(--white)', color: 'var(--gray-800)', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="dash-source-list">
                    {sourceData.map((s, i) => (
                      <div key={i} className="dash-source-row">
                        <div className="dash-source-dot" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="dash-source-name">{s.source}</span>
                        <span className="dash-source-count">{s.count}</span>
                        <span className="dash-source-pct">{totalSourceLeads > 0 ? Math.round((s.count / totalSourceLeads) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="dash-empty">No source data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* ── Project Leads + Followups Row ── */}
        <div className="dash-row-2">
          {/* Project-wise Leads */}
          <div className="card">
            <div className="card__header">
              <h3 className="card__title">Project-wise Leads</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => navigate('/dynamic-fields', { state: { tab: 'projects' } })}>Projects <ArrowUpRight size={14} /></button>
            </div>
            <div className="card__body" style={{ padding: 0 }}>
              {projectData.length > 0 ? (
                <div className="dash-project-list">
                  {projectData.map((p, i) => (
                    <div key={p.project_id} className="dash-project-row">
                      <div className="dash-project-rank">{i + 1}</div>
                      <div className="dash-project-info">
                        <span className="dash-project-name">{p.name}</span>
                        <div className="dash-project-bar-bg">
                          <div className="dash-project-bar" style={{ width: `${Math.min(100, (p.count / (projectData[0]?.count || 1)) * 100)}%` }} />
                        </div>
                      </div>
                      <span className="dash-project-count">{p.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dash-empty">No project leads yet</div>
              )}
            </div>
          </div>

          {/* Upcoming Follow-ups */}
          <div className="card">
            <div className="card__header">
              <h3 className="card__title">Upcoming Follow-ups</h3>
              <button className="btn btn--ghost btn--sm" onClick={() => navigate('/followup/upcoming')}>View All <ArrowUpRight size={14} /></button>
            </div>
            <div className="card__body" style={{ padding: 0 }}>
              {upcomingFollowups.length > 0 ? (
                <div className="dash-followup-list">
                  {upcomingFollowups.map((fu, i) => (
                    <div key={fu.lf_id || i} className="dash-followup-row">
                      <div className="dash-followup-time">
                        <Calendar size={13} />
                        <span>{formatDate(fu.followup_dt)}</span>
                        <Clock size={12} />
                        <span>{formatTime(fu.followup_dt)}</span>
                      </div>
                      <div className="dash-followup-info">
                        <span className="dash-followup-name">{fu.lead_name}</span>
                        {fu.note && <span className="dash-followup-note">{fu.note}</span>}
                      </div>
                      {fu.status_name && (
                        <span className="dash-followup-badge" style={{ color: fu.status_color || '#6b7280', background: (fu.status_color || '#6b7280') + '15' }}>
                          {fu.status_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dash-empty">No upcoming follow-ups</div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
